import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { fetchPolicies, fetchPremiumCalendar, deletePolicy } from "../lib/api";
import { normalizeDetail } from "../lib/errors";
import { PolicyCard } from "../components/policies/PolicyCard";
import { PolicyDialog } from "../components/policies/PolicyDialog";
import { PolicyEmptyState } from "../components/policies/PolicyEmptyState";
import { PremiumCalendarSidebar } from "../components/policies/PremiumCalendarSidebar";
import { POLICY_TYPES } from "../components/policies/policyShared";

/**
 * User policies dashboard — list of enrolled insurance policies grouped by type + a
 * chronological premium payment calendar sidebar for the next 12 months.
 *
 * 08-Feb-2026 refactor: this page was ~860 lines and mixed the shell, card, sidebar, empty
 * state, and a 340-line add/edit modal. Each concern is now in `components/policies/*.jsx`
 * so the shell here is ~90 lines and each piece can be modified without cross-effects.
 */
export function PoliciesPage() {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { mode: 'create' | 'edit', policy? }

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([fetchPolicies(), fetchPremiumCalendar(12)]);
      setPolicies(p);
      setCalendar(c);
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't load policies."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const grouped = useMemo(() => {
    const map = new Map(POLICY_TYPES.map((t) => [t.value, []]));
    for (const p of policies) {
      (map.get(p.type) || map.get("other")).push(p);
    }
    return map;
  }, [policies]);

  async function handleDelete(policy) {
    if (!window.confirm(`Delete "${policy.name}"? This cannot be undone.`)) return;
    try {
      await deletePolicy(policy.id);
      toast.success("Policy deleted.");
      await refresh();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't delete."));
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <button type="button" onClick={() => navigate(-1)} className="text-xs text-muted hover:text-gold" data-testid="page-back">
            ← Go back
          </button>
          <h1 className="mt-2 font-serif text-3xl text-text sm:text-4xl">
            My <span className="text-gold">Policies</span>
          </h1>
          <p className="mt-1 text-sm text-muted">
            Track your term life, health, accident and motor policies. Sensitive fields are
            encrypted at rest.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/app/policies/coverage-gap"
            data-testid="policies-coverage-gap-link"
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-2 text-sm text-muted hover:border-gold hover:text-gold"
          >
            Coverage gap →
          </Link>
          <Link
            to="/app/policies/analyze"
            data-testid="policies-analyze-link"
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-2 text-sm text-muted hover:border-gold hover:text-gold"
          >
            Analyze a policy →
          </Link>
          <Link
            to="/app/policies/calendar"
            data-testid="policies-calendar-link"
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-2 text-sm text-muted hover:border-gold hover:text-gold"
          >
            Payment calendar →
          </Link>
          <Button
            onClick={() => setDialog({ mode: "create" })}
            data-testid="policy-add-btn"
            className="gap-2"
          >
            <Plus className="h-4 w-4" /> Add policy
          </Button>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-8">
          {loading ? (
            <p className="text-sm text-muted">Loading your policies…</p>
          ) : policies.length === 0 ? (
            <PolicyEmptyState onAdd={() => setDialog({ mode: "create" })} />
          ) : (
            POLICY_TYPES.map((t) => {
              const list = grouped.get(t.value) || [];
              if (list.length === 0) return null;
              return (
                <section key={t.value}>
                  <div className="mb-3 flex items-center gap-2">
                    <t.icon className="h-4 w-4 text-gold" />
                    <h2 className="text-sm font-semibold uppercase tracking-widest text-text">
                      {t.label}
                    </h2>
                    <span className="text-xs text-muted">
                      · {list.length} {list.length === 1 ? "policy" : "policies"}
                    </span>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {list.map((p) => (
                      <PolicyCard
                        key={p.id}
                        policy={p}
                        onEdit={() => setDialog({ mode: "edit", policy: p })}
                        onDelete={() => handleDelete(p)}
                      />
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>

        <PremiumCalendarSidebar items={calendar} loading={loading} />
      </div>

      {dialog && (
        <PolicyDialog
          initial={dialog.policy}
          mode={dialog.mode}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

export default PoliciesPage;
